import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, X, Check, Sparkles } from './Icons.jsx';
import { segments, disciplines, roles, setSegments, updateProfile } from '../lib/segment.js';
import { useAuth } from '../lib/auth.jsx';

export default function Onboarding({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    role: 'student',
    segments: [],
    discipline: 'medicine',
    ucatDate: '',
    targetScore: 2400
  });
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!open) return null;

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  const persistProfile = () => {
    if (data.role === 'student') {
      if (data.segments && data.segments.length > 0) setSegments(data.segments);
      updateProfile({
        role: data.role,
        discipline: data.discipline,
        ucatDate: data.ucatDate || null,
        targetScore: data.targetScore
      });
    }
  };

  const finish = () => {
    persistProfile();
    onClose();
    if (data.role !== 'student') navigate(roles[data.role].path);
  };

  const createAccount = () => {
    persistProfile();
    onClose();
    navigate(`/signup?role=${data.role}`);
  };

  const isGraduate = data.segments?.includes('uk-graduate') && data.segments.length === 1;
  const daysToUcat = data.ucatDate
    ? Math.max(0, Math.ceil((new Date(data.ucatDate).getTime() - Date.now()) / 86400000))
    : null;
  const steps = [
    { id: 'role', valid: () => !!data.role },
    { id: 'segment', valid: () => data.segments.length > 0, only: data.role === 'student' },
    { id: 'discipline', valid: () => !!data.discipline, only: data.role === 'student' },
    { id: 'date', valid: () => true, only: data.role === 'student' && !isGraduate },
    { id: 'target', valid: () => true, only: data.role === 'student' && !isGraduate },
    // Soft sign-up ask — only for logged-out visitors. Never blocks: there's
    // always a "keep browsing as a guest" escape on this step.
    { id: 'save', valid: () => true, only: !user }
  ].filter((s) => s.only !== false);
  const current = steps[step];
  const isLast = step === steps.length - 1;

  const saveCopy = {
    student: {
      headline: daysToUcat != null && daysToUcat > 0
        ? `Your UCAT is ${daysToUcat} ${daysToUcat === 1 ? 'day' : 'days'} away.`
        : 'Your study setup is ready.',
      sub: 'Create a free account to save it — otherwise it lives only on this device and disappears if you clear your browser.',
      perks: ['Save your countdown, target score and study plan', 'Track every drill score over time', 'Pick up on any device — phone, laptop, library PC'],
    },
    parent: {
      headline: 'Stay in the loop.',
      sub: 'Create a free account to track your child\'s progress and get plain-English summaries of where they\'re plateauing.',
      perks: ['Link to your child\'s account', 'See drill progress and weak spots', 'Weekly summary emails'],
    },
    teacher: {
      headline: 'Set up your cohort.',
      sub: 'Create a free account to add your students and see their drill progress in one dashboard.',
      perks: ['Add and manage your students', 'See cohort-wide weak spots', 'Export progress for parents\' evenings'],
    },
  }[data.role] || {};

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Skip" style={closeBtn}><X width="16" height="16" /></button>

        <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
          {steps.map((_, i) => (
            <div key={i} style={{ flex: 1, height: 4, borderRadius: 99, background: i <= step ? 'var(--brand-purple)' : 'var(--card-soft)' }} />
          ))}
        </div>

        {current.id === 'role' && (
          <>
            <div style={kicker}>Welcome to Medi</div>
            <h2 style={h2}>Who are you?</h2>
            <p style={subtle}>Pick the right account so the platform talks to you in the right voice.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(roles).map(([id, r]) => (
                <button key={id} onClick={() => setData({ ...data, role: id })} style={optBtn(data.role === id)}>
                  <span style={{ fontSize: 24 }}>{r.emoji}</span>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <strong style={{ fontSize: 15 }}>{r.label}</strong>
                    {id === 'student' && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>Applying to medicine or dentistry</div>}
                    {id === 'parent' && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>Supporting your child through the application</div>}
                    {id === 'teacher' && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>Sixth-form lead, UCAS advisor, careers staff</div>}
                  </div>
                  {data.role === id && <Check width="18" height="18" />}
                </button>
              ))}
            </div>
          </>
        )}

        {current.id === 'segment' && (
          <>
            <div style={kicker}>Step 2 of {steps.length}</div>
            <h2 style={h2}>Which path are you on?</h2>
            <p style={subtle}>Pick all that apply — international + gap year, undergrad + international, etc. all combine.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.values(segments).map((s) => {
                const selected = data.segments.includes(s.id);
                const toggle = () => {
                  const next = selected
                    ? data.segments.filter((id) => id !== s.id)
                    : [...data.segments, s.id];
                  setData({ ...data, segments: next });
                };
                return (
                  <button key={s.id} onClick={toggle} style={optBtn(selected)}>
                    <span style={{ fontSize: 22 }}>{s.emoji}</span>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <strong style={{ fontSize: 14.5 }}>{s.label}</strong>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{s.sub}</div>
                    </div>
                    <div style={{
                      width: 22, height: 22, borderRadius: 6,
                      border: `2px solid ${selected ? 'var(--brand-purple)' : 'var(--border-strong)'}`,
                      background: selected ? 'var(--brand-purple)' : '#fff',
                      display: 'grid', placeItems: 'center', flexShrink: 0
                    }}>
                      {selected && <Check width="14" height="14" style={{ color: '#fff' }} />}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {current.id === 'discipline' && (
          <>
            <div style={kicker}>Step 3 of {steps.length}</div>
            <h2 style={h2}>Medicine or dentistry?</h2>
            <p style={subtle}>Same UCAT, different schools and reflections.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {Object.entries(disciplines).map(([id, d]) => (
                <button key={id} onClick={() => setData({ ...data, discipline: id })} style={tileBtn(data.discipline === id)}>
                  <span style={{ fontSize: 38 }}>{d.emoji}</span>
                  <strong style={{ fontSize: 16, marginTop: 6 }}>{d.label}</strong>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{d.degree}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {current.id === 'date' && (
          <>
            <div style={kicker}>Step 4 of {steps.length}</div>
            <h2 style={h2}>When do you sit your UCAT?</h2>
            <input type="date" value={data.ucatDate} onChange={(e) => setData({ ...data, ucatDate: e.target.value })}
              style={{ width: '100%', padding: '12px 14px', fontSize: 15, border: '1px solid var(--border-strong)', borderRadius: 10, marginTop: 8, fontFamily: 'inherit' }} />
            <p style={{ marginTop: 10, color: 'var(--text-muted)', fontSize: 13 }}>Optional — but you'll get a daily countdown and study plan.</p>
          </>
        )}

        {current.id === 'target' && (
          <>
            <div style={kicker}>Step 5 of {steps.length}</div>
            <h2 style={h2}>Target cognitive total (VR + DM + QR)</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 10 }}>
              <input type="range" min="1200" max="2700" step="10" value={data.targetScore}
                onChange={(e) => setData({ ...data, targetScore: Number(e.target.value) })}
                style={{ flex: 1, accentColor: 'var(--brand-purple)' }} />
              <input type="number" min="1200" max="2700" value={data.targetScore}
                onChange={(e) => setData({ ...data, targetScore: Math.max(1200, Math.min(2700, Number(e.target.value) || 0)) })}
                style={{ width: 90, padding: '8px 10px', fontSize: 17, fontWeight: 700, border: '1px solid var(--border-strong)', borderRadius: 8, textAlign: 'right' }} />
            </div>
            <p style={{ marginTop: 10, color: 'var(--text-muted)', fontSize: 13 }}>Each subtest is 300–900. Most offer-holders aim for 2400+ (800 per subtest avg).</p>
          </>
        )}

        {current.id === 'save' && (
          <>
            <div style={kicker}>You're all set</div>
            <h2 style={h2}>{saveCopy.headline}</h2>
            <p style={subtle}>{saveCopy.sub}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '8px 0 22px' }}>
              {(saveCopy.perks || []).map((perk) => (
                <div key={perk} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--text-2)' }}>
                  <span style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--accent-soft)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                    <Check width="13" height="13" style={{ color: 'var(--brand-purple)' }} />
                  </span>
                  {perk}
                </div>
              ))}
            </div>
            <button onClick={createAccount} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: 15, padding: '12px 16px' }}>
              Create my free account <ArrowRight width="16" height="16" />
            </button>
            <button onClick={finish} style={{ width: '100%', marginTop: 12, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13.5, fontWeight: 600 }}>
              Maybe later — keep browsing as a guest
            </button>
            <p style={{ margin: '14px 0 0', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
              Free forever. Takes 30 seconds. No card needed.
            </p>
          </>
        )}

        {current.id !== 'save' && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 22 }}>
            <button onClick={step === 0 ? onClose : back} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {step === 0 ? 'Skip' : <><ArrowLeft width="14" height="14" />Back</>}
            </button>
            <button onClick={isLast ? finish : next} disabled={!current.valid()} className="btn btn-primary" style={{ opacity: current.valid() ? 1 : 0.5 }}>
              {isLast ? <>Done <Sparkles width="14" height="14" /></> : <>Next <ArrowRight width="14" height="14" /></>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const backdrop = { position: 'fixed', inset: 0, background: 'rgba(35, 20, 63, 0.55)', backdropFilter: 'blur(4px)', display: 'grid', placeItems: 'center', zIndex: 200, padding: 16 };
const panel = { position: 'relative', background: '#fff', borderRadius: 18, padding: 28, width: '100%', maxWidth: 460, boxShadow: '0 30px 80px rgba(0, 0, 0, 0.3)' };
const closeBtn = { position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 6 };
const kicker = { fontSize: 11, fontWeight: 700, color: 'var(--brand-purple)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6 };
const h2 = { margin: '0 0 4px', fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' };
const subtle = { margin: '0 0 18px', color: 'var(--text-muted)', fontSize: 14 };

function optBtn(active) {
  return {
    padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
    background: active ? 'var(--accent-soft)' : '#fff',
    border: `2px solid ${active ? 'var(--brand-purple)' : 'var(--border)'}`,
    borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
    color: 'var(--text)'
  };
}
function tileBtn(active) {
  return {
    padding: '18px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    background: active ? 'var(--accent-soft)' : '#fff',
    border: `2px solid ${active ? 'var(--brand-purple)' : 'var(--border)'}`,
    borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text)'
  };
}
